import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  CheckCircle, XCircle, Clock, Eye, Trash2, 
  Calendar, MapPin, User, Mail, Globe, Building,
  AlertTriangle, Shield, Loader2, ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSubmissionsPage() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [stats, setStats] = useState({ total: 0, pending: 0 });
  const [expandedId, setExpandedId] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/submissions?status=${statusFilter}&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmissions(response.data.submissions || []);
      setStats({
        total: response.data.total || 0,
        pending: response.data.pending_count || 0
      });
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [statusFilter]);

  const handleApprove = async (submissionId, eventName) => {
    if (!window.confirm(`Approve "${eventName}" and create the event?`)) return;
    
    setProcessingId(submissionId);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/submissions/${submissionId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Event approved! ${response.data.event_url}`);
      fetchSubmissions();
    } catch (error) {
      console.error('Error approving submission:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve submission');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (submissionId, eventName, contactEmail) => {
    if (!window.confirm(
      `Reject and delete "${eventName}"?\n\nA rejection email will be sent to ${contactEmail}.`
    )) return;
    
    setProcessingId(submissionId);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${BACKEND_URL}/api/admin/submissions/${submissionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Submission rejected and organiser notified');
      fetchSubmissions();
    } catch (error) {
      console.error('Error rejecting submission:', error);
      toast.error('Failed to reject submission');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium flex items-center gap-1"><Clock className="w-3 h-3" />Pending</span>;
      case 'approved':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" />Approved</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium flex items-center gap-1"><XCircle className="w-3 h-3" />Rejected</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Event Submissions</h1>
        <p className="text-gray-600 mt-1">Review and approve event listing requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              <p className="text-sm text-gray-500">Pending Review</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Building className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Total Submissions</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Filter by Status</p>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="mt-1 text-sm border rounded px-2 py-1"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="">All</option>
              </select>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Submissions List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : submissions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No submissions to review</h3>
            <p className="text-gray-500 mt-1">All caught up! Check back later.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {submissions.map((submission) => (
            <Card key={submission.id} className={`overflow-hidden ${submission.status === 'pending' ? 'border-yellow-300' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(submission.status)}
                      <span className="text-xs text-gray-400">
                        Submitted {formatDateTime(submission.created_at)}
                      </span>
                    </div>
                    <CardTitle className="text-xl">{submission.eventName}</CardTitle>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {submission.city}
                      </span>
                      <span className="flex items-center gap-1">
                        <Building className="w-4 h-4" />
                        {submission.venueName || 'TBC'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(submission.startDate)}
                        {submission.endDate && submission.endDate !== submission.startDate && ` - ${formatDate(submission.endDate)}`}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  {submission.status === 'pending' && (
                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => handleApprove(submission.id, submission.eventName)}
                        disabled={processingId === submission.id}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        {processingId === submission.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleReject(submission.id, submission.eventName, submission.contactEmail)}
                        disabled={processingId === submission.id}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                {/* Short Description */}
                <p className="text-gray-600 mb-4">{submission.shortDescription}</p>
                
                {/* Expand/Collapse */}
                <button
                  onClick={() => setExpandedId(expandedId === submission.id ? null : submission.id)}
                  className="flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700 font-medium"
                >
                  {expandedId === submission.id ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      View Full Details
                    </>
                  )}
                </button>
                
                {/* Expanded Details */}
                {expandedId === submission.id && (
                  <div className="mt-4 pt-4 border-t space-y-4">
                    {/* Organiser Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Organiser Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Name:</span>
                          <p className="font-medium">{submission.organiserName}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Email:</span>
                          <p className="font-medium">
                            <a href={`mailto:${submission.contactEmail}`} className="text-blue-600 hover:underline flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {submission.contactEmail}
                            </a>
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">Website:</span>
                          <p className="font-medium">
                            {submission.organiserWebsite ? (
                              <a href={submission.organiserWebsite} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                {submission.organiserWebsite}
                              </a>
                            ) : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Event Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Event Type:</span>
                        <p className="font-medium">{submission.eventType}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Official URL:</span>
                        <p className="font-medium">
                          {submission.officialUrl ? (
                            <a href={submission.officialUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" />
                              {submission.officialUrl}
                            </a>
                          ) : 'N/A'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Long Description */}
                    {submission.longDescription && (
                      <div>
                        <span className="text-gray-500 text-sm">Full Description:</span>
                        <p className="mt-1 text-gray-700 whitespace-pre-wrap">{submission.longDescription}</p>
                      </div>
                    )}
                    
                    {/* Accommodation Help */}
                    {submission.needsAccommodationHelp && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-green-800 font-medium">Organiser wants help with delegate/sponsor accommodation</span>
                      </div>
                    )}
                    
                    {/* Technical Info */}
                    <div className="bg-gray-100 rounded-lg p-3 text-xs text-gray-500">
                      <div className="flex items-center gap-2 mb-1">
                        <Shield className="w-3 h-3" />
                        <span className="font-medium">Submission Metadata</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                        <div>
                          <span className="text-gray-400">ID:</span>
                          <p className="font-mono">{submission.id.slice(0, 8)}...</p>
                        </div>
                        <div>
                          <span className="text-gray-400">IP Address:</span>
                          <p className="font-mono">{submission.ip_address || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Submitted:</span>
                          <p>{formatDateTime(submission.created_at)}</p>
                        </div>
                        {submission.reviewed_at && (
                          <div>
                            <span className="text-gray-400">Reviewed:</span>
                            <p>{formatDateTime(submission.reviewed_at)} by {submission.reviewed_by}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
